import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { User, Mail, Lock, Building2, Phone, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { authService } from "../services/api";
import { toast } from "sonner";

const planNames = {
  basico: "Básico",
  pro: "Pro",
  premium: "Premium",
  agencia: "Agencia"
};

export const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get("plan");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    company_name: "",
    phone: ""
  });

  // Redirect if already logged in
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate("/admin/dashboard");
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setLoading(true);
    try {
      // Register the user - this saves token to localStorage and sets axios header
      await authService.register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        company_name: formData.company_name || null,
        phone: formData.phone,
        plan: selectedPlan || null,
        utm_source: searchParams.get("utm_source") || (searchParams.get("fbclid") ? "facebook" : null),
        utm_medium: searchParams.get("utm_medium") || (searchParams.get("fbclid") ? "paid" : null),
        utm_campaign: searchParams.get("utm_campaign") || null,
        referrer: document.referrer || null,
        fbclid: searchParams.get("fbclid") || null
      });
      
      toast.success("¡Cuenta creada! Verifica tu email para continuar.");
      
      // Redirect to email verification page with plan parameter
      const verifyUrl = selectedPlan ? `/verify-email?plan=${selectedPlan}` : "/verify-email";
      window.location.href = verifyUrl;
      
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.response?.data?.detail || "Error al crear cuenta");
      setLoading(false);
    }
  };

  const planFeatures = [
    "Landing pages profesionales",
    "Formulario de contacto integrado",
    "Pago seguro con Stripe",
    "Cancela cuando quieras"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left side - Benefits */}
        <div className="hidden lg:block text-white p-8">
          <Link to="/" className="flex items-center space-x-2 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_proplandpro/artifacts/lv2qzyun_inmopages-fondooscuro.png" 
              alt="InmoPages" 
              className="h-10"
            />
          </Link>
          
          <h1 className="text-4xl font-bold mb-4">
            Crea landing pages profesionales para tus propiedades
          </h1>
          <p className="text-gray-400 text-lg mb-8">
            Únete a cientos de agentes inmobiliarios que ya usan nuestra plataforma para vender más rápido.
          </p>

          <div className="space-y-4">
            {planFeatures.map((feature, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-brand-purple/20 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-brand-purple" />
                </div>
                <span className="text-gray-300">{feature}</span>
              </div>
            ))}
          </div>

          <div className="mt-12 p-6 bg-white/5 rounded-xl border border-white/10">
            <p className="text-sm text-gray-400 mb-2">Lo que dicen nuestros usuarios</p>
            <p className="text-lg italic">
              "Desde que uso esta plataforma, mis propiedades se venden en la mitad del tiempo. Las landing pages son muy profesionales."
            </p>
            <p className="text-brand-purple font-semibold mt-3">— Carlos Mendoza, Agente Inmobiliario</p>
          </div>
        </div>

        {/* Right side - Register form */}
        <Card className="w-full shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden flex justify-center mb-4">
              <Link to="/">
                <img 
                  src="https://customer-assets.emergentagent.com/job_proplandpro/artifacts/d99lgtpt_inmopages-fondoblanco.png" 
                  alt="InmoPages" 
                  className="h-12"
                />
              </Link>
            </div>
            <CardTitle className="text-2xl">Crear Cuenta</CardTitle>
            <CardDescription>
              {selectedPlan && planNames[selectedPlan] 
                ? (
                  <span>
                    Plan seleccionado: <span className="font-semibold text-brand-purple">{planNames[selectedPlan]}</span>
                  </span>
                )
                : "Regístrate y elige tu plan para comenzar"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <Label htmlFor="name">Nombre completo *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Juan Pérez"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <Label htmlFor="phone">WhatsApp *</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+52 333 123 4567"
                      className="pl-10"
                      required
                      data-testid="register-phone"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="company">Empresa / Inmobiliaria</Label>
                <div className="relative mt-1">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="company"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Mi Inmobiliaria"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="tu@email.com"
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <Label htmlFor="password">Contraseña *</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <Label htmlFor="confirmPassword">Confirmar *</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="••••••••"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-brand-purple hover:bg-brand-pink text-white h-12 text-base font-semibold"
                disabled={loading}
              >
                {loading ? "Procesando..." : "Crear Cuenta"}
              </Button>

              {selectedPlan && (
                <p className="text-xs text-center text-gray-500 bg-gray-50 p-2 rounded">
                  Después de verificar tu email, serás redirigido a Stripe para completar el pago.
                </p>
              )}

              <p className="text-xs text-center text-gray-500 mt-4">
                Al registrarte, aceptas nuestros términos de servicio y política de privacidad.
              </p>
            </form>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-3 text-gray-500">o regístrate con</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 text-base font-medium border-gray-300 hover:bg-gray-50"
              onClick={() => {
                const callbackUri = window.location.origin + '/api/auth/google/callback';
                const planParam = selectedPlan ? `&plan=${selectedPlan}` : '';
                window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google?callback_uri=${encodeURIComponent(callbackUri)}${planParam}`;
              }}
              data-testid="google-register-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Registrarse con Google
            </Button>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                ¿Ya tienes cuenta?{" "}
                <Link to="/login" className="text-brand-purple hover:text-brand-pink font-semibold">
                  Iniciar Sesión
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
